import React, { useState, useEffect, useCallback } from 'react';
import { FaSearch, FaTimes, FaSortUp, FaSortDown, FaDownload } from 'react-icons/fa';
import Pagination from '../components/Pagination';
import DateRangeFilter from '../components/DateRangeFilter';
import { fetchBooksList, exportBooksCsv, fetchBookStatistics } from '../services/api';
import { formatAgeRating } from '../utils/formatters';
import '../components/DataTable.css';
import './DataPages.css';
import './Pages.css';

const toUTCDateStr = (date) => `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;

const getDefaultDateRange = () => {
  const end = new Date();
  const start = new Date();
  start.setUTCDate(start.getUTCDate() - 29);
  return {
    startDate: toUTCDateStr(start),
    endDate: toUTCDateStr(end)
  };
};

const BooksDetails = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [sortOrder, setSortOrder] = useState('DESC');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [filterLanguage, setFilterLanguage] = useState('');
  const [filterAgeRating, setFilterAgeRating] = useState('');
  const [startDate, setStartDate] = useState(getDefaultDateRange().startDate);
  const [endDate, setEndDate] = useState(getDefaultDateRange().endDate);
  const [exporting, setExporting] = useState(false);
  const [languages, setLanguages] = useState([]);
  const [ageRatings, setAgeRatings] = useState([]);
  const pageSize = 100;

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await fetchBooksList(page, pageSize, sortOrder, searchTerm, filterLanguage, filterAgeRating, startDate, endDate);
      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [page, sortOrder, searchTerm, filterLanguage, filterAgeRating, startDate, endDate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    fetchBookStatistics().then(stats => {
      setLanguages(Object.keys(stats.booksByLanguage).sort());
      setAgeRatings(Object.keys(stats.booksByAgeRating).sort());
    }).catch(() => {});
  }, []);

  const handleSearch = () => {
    setSearchTerm(searchInput);
    setPage(1);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleSearchClear = () => {
    setSearchInput('');
    setSearchTerm('');
    setPage(1);
  };

  const handleSortToggle = () => {
    setSortOrder(prev => prev === 'DESC' ? 'ASC' : 'DESC');
    setPage(1);
  };

  const handleLanguageChange = (e) => {
    setFilterLanguage(e.target.value);
    setPage(1);
  };

  const handleAgeRatingChange = (e) => {
    setFilterAgeRating(e.target.value);
    setPage(1);
  };

  const handleDateApply = () => {
    setPage(1);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const handleDownloadCsv = async () => {
    try {
      setExporting(true);
      const response = await exportBooksCsv(sortOrder, searchTerm, filterLanguage, filterAgeRating, startDate, endDate);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const now = new Date();
      const timestamp = `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}_${String(now.getHours()).padStart(2,'0')}h${String(now.getMinutes()).padStart(2,'0')}m${String(now.getSeconds()).padStart(2,'0')}s`;
      const filename = `Books_${timestamp}.csv`;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('CSV export failed:', err);
    } finally {
      setExporting(false);
    }
  };

  const items = data ? data.items : [];

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="page-header-content">
          <h1>Books</h1>
        </div>
      </div>

      <div className="data-page-content">
        {error && <div className="table-empty">Error: {error}</div>}

        {!error && (
          <>
            <div className="filters-container">
              <DateRangeFilter
                startDate={startDate}
                endDate={endDate}
                onStartDateChange={setStartDate}
                onEndDateChange={setEndDate}
                onApply={handleDateApply}
                earliestDate={data?.earliestDate}
                latestDate={data?.latestDate}
              />
            </div>

            <div className="data-page-controls">
              <div className="table-search-wrapper">
                <FaSearch className="table-search-icon" size={14} />
                <input
                  type="text"
                  className="table-search-input"
                  placeholder="Search..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                />
                {searchInput && (
                  <button className="table-search-clear" onClick={handleSearchClear}>
                    <FaTimes size={14} />
                  </button>
                )}
                <button className="table-search-btn" onClick={handleSearch} title="Search">
                  <FaSearch size={14} />
                </button>
              </div>

              <div className="data-page-filters">
                <select
                  value={filterLanguage}
                  onChange={handleLanguageChange}
                  className="data-page-filter-select"
                >
                  <option value="">All Languages</option>
                  {languages.map(lang => (
                    <option key={lang} value={lang}>{lang}</option>
                  ))}
                </select>

                <select
                  value={filterAgeRating}
                  onChange={handleAgeRatingChange}
                  className="data-page-filter-select"
                >
                  <option value="">All Age Ratings</option>
                  {ageRatings.map(ar => (
                    <option key={ar} value={formatAgeRating(ar)}>{formatAgeRating(ar)}</option>
                  ))}
                </select>
              </div>

              <button className="btn-primary" onClick={handleDownloadCsv} title="Download CSV" disabled={exporting}>
                <FaDownload size={14} />
                <span>{exporting ? 'Exporting ...' : 'CSV'}</span>
              </button>

              {data && (
                <div className="control-badge" style={{ marginLeft: 'auto' }}>
                  <span className="badge-label">Viewing</span>
                  <span className="badge-value">{data.totalCount.toLocaleString()}</span>
                </div>
              )}
            </div>

            {loading && <div className="table-loading">Loading ...</div>}

            {!loading && items.length > 0 && (
              <div className="data-page-table-card">
                <div className="data-table-wrapper">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Book ID</th>
                        <th className="sortable" onClick={handleSortToggle}>
                          Book Publication Date
                          <span className="sort-indicator">
                            {sortOrder === 'ASC' ? <FaSortUp size={12} /> : <FaSortDown size={12} />}
                          </span>
                        </th>
                        <th>Book Language</th>
                        <th>Book Length</th>
                        <th>Reading Level</th>
                        <th>Book Type</th>
                        <th>Book Age Rating</th>
                        <th>Illustrator ID</th>
                        <th>Number of Illustrations</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((book, index) => (
                        <tr key={`${book.bookId}-${book.language}-${index}`}>
                          <td>{book.bookId}</td>
                          <td>{formatDate(book.publicationDate)}</td>
                          <td>{book.language || '-'}</td>
                          <td>{book.length.toLocaleString()}</td>
                          <td>{book.readingLevel}</td>
                          <td>{book.bookType || '-'}</td>
                          <td>{formatAgeRating(book.ageRating) || '-'}</td>
                          <td>{book.illustratorId || '-'}</td>
                          <td>{book.numberOfIllustrations}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <Pagination
                  page={data.page}
                  totalPages={data.totalPages}
                  totalCount={data.totalCount}
                  pageSize={data.pageSize}
                  onPageChange={setPage}
                />
              </div>
            )}

            {!loading && data && items.length === 0 && (
              <div className="table-empty">
                {searchTerm || filterLanguage || filterAgeRating || startDate || endDate
                  ? 'No results found for the current filters.'
                  : 'No books found.'}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BooksDetails;
